import { computed, readonly, ref } from 'vue';

const CHECK_INTERVAL_MS = 5 * 60_000;
const REQUEST_TIMEOUT_MS = 10_000;

interface VersionGuardOptions {
  version: string;
  enabled: boolean;
  apiUrl: () => string;
  appBaseUrl: string;
  reload?: () => void;
}

function validVersion(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0 && value !== 'unknown';
}

/** Remove only this app's worker/caches. IndexedDB holds unsynced attendance and stays intact. */
async function clearAppCaches(scope: string): Promise<void> {
  if ('serviceWorker' in navigator) {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.filter(r => r.scope === scope).map(r => r.unregister()));
  }
  if ('caches' in window) {
    const names = await caches.keys();
    await Promise.all(names
      .filter(name => name === 'parcours-api' ||
        (name.startsWith('workbox-precache-') && name.endsWith(scope)))
      .map(name => caches.delete(name)));
  }
}

/** #1014: a mismatch only prompts. Reloading always requires an explicit user action. */
export function createVersionGuard(options: VersionGuardOptions) {
  const availableVersion = ref<string | null>(null);
  const reloading = ref(false);
  const reloadFailed = ref(false);
  const attempted = ref(false);
  const enabled = options.enabled && validVersion(options.version);
  const scope = new URL(options.appBaseUrl, window.location.href).href;
  let interval: ReturnType<typeof setInterval> | undefined;
  let request: Promise<string | null> | undefined;
  let controller: AbortController | undefined;

  // Per tab AND per pair: alternating backend pods must not repeatedly reload an old bundle.
  const attemptKey = (version: string) =>
    `parcours:version-reload:${JSON.stringify([options.version, version])}`;

  async function fetchVersion(): Promise<string | null> {
    controller = new AbortController();
    const timeout = setTimeout(() => controller?.abort(), REQUEST_TIMEOUT_MS);
    try {
      const url = new URL(`${options.apiUrl().replace(/\/$/, '')}/core/settings`, window.location.href);
      url.searchParams.set('__parcours_version_check', '1');
      // Anonymous endpoint, intentionally outside ApiFetch: network errors must stay silent.
      const response = await fetch(url, {
        cache: 'no-store', credentials: 'include', signal: controller.signal,
      });
      if (!response.ok) return null;
      const settings: unknown = await response.json();
      const version = settings && typeof settings === 'object' && 'version' in settings
        ? settings.version : undefined;
      if (!validVersion(version)) return null;

      availableVersion.value = version === options.version ? null : version;
      try {
        attempted.value = sessionStorage.getItem(attemptKey(version)) === '1';
      } catch {
        // Reading settings must also work when browser storage is restricted.
      }
      return version;
    } catch {
      // Offline, a timeout, or an unavailable backend must never disrupt the cached app.
      return null;
    } finally {
      clearTimeout(timeout);
      controller = undefined;
    }
  }

  function check(): Promise<string | null> {
    if (!enabled || !navigator.onLine) return Promise.resolve(null);
    // Reconnect/visibility/interval events can coincide; share the in-flight probe.
    request ??= fetchVersion().finally(() => { request = undefined; });
    return request;
  }

  async function reload(): Promise<void> {
    if (!enabled || reloading.value || !availableVersion.value || attempted.value) return;
    reloading.value = true;
    reloadFailed.value = false;
    try {
      // The user may have left the banner open for hours. Recheck before touching offline caches.
      const version = await check();
      if (!version) {
        reloadFailed.value = true;
        return;
      }
      if (!availableVersion.value || attempted.value) return;

      // Persist before any destructive cleanup. If storage is blocked, leave the app intact.
      const key = attemptKey(version);
      sessionStorage.setItem(key, '1');
      try {
        await clearAppCaches(scope);
      } catch (error) {
        sessionStorage.removeItem(key);
        throw error;
      }
      attempted.value = true;
      (options.reload ?? (() => window.location.reload()))();
    } catch {
      reloadFailed.value = true;
    } finally {
      reloading.value = false;
    }
  }

  const onOnline = () => { void check(); };
  const onVisible = () => { if (document.visibilityState === 'visible') void check(); };

  function start(): void {
    if (!enabled || interval !== undefined) return;
    void check();
    interval = setInterval(onOnline, CHECK_INTERVAL_MS);
    window.addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', onVisible);
  }

  function stop(): void {
    clearInterval(interval);
    interval = undefined;
    controller?.abort();
    window.removeEventListener('online', onOnline);
    document.removeEventListener('visibilitychange', onVisible);
  }

  return {
    availableVersion: readonly(availableVersion),
    reloading: readonly(reloading),
    reloadFailed: readonly(reloadFailed),
    alreadyReloaded: computed(() => !!availableVersion.value && attempted.value),
    check, reload, start, stop,
  };
}
